/**
 * TPL MICRO-DESIGN ELEMENTS - Variant-Aware Updates
 *
 * Listens to Shopify's variant-change events and updates micro-design
 * components with variant-specific data from metafields.
 *
 * Components updated:
 * - Edition Badge (inventory counts, SKU)
 * - Technical Specs (dimensions, paper, method, finish)
 */

if (!customElements.get('tpl-micro-design-updater')) {
  customElements.define('tpl-micro-design-updater', class TPLMicroDesignUpdater extends HTMLElement {
    constructor() {
      super();
      this.productData = null;
      this.currentVariantId = null;
    }

    connectedCallback() {
      // Get initial product data from JSON script tag
      const productJson = document.querySelector('[data-product-json]');
      if (productJson) {
        try {
          this.productData = JSON.parse(productJson.textContent);
        } catch (e) {
          console.error('TPL Micro-Design: Failed to parse product data', e);
        }
      }

      // Subscribe to variant change events — guard against missing pub/sub
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          this.onVariantChange(event);
        });
      }

      // Get initial variant from URL or first available
      this.initializeFromUrl();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    /**
     * Initialize from URL parameters (e.g., ?variant=123456)
     */
    initializeFromUrl() {
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (variantId && this.productData) {
        const variant = this.productData.variants.find(v => v.id === parseInt(variantId));
        if (variant) {
          this.updateComponents(variant);
        }
      }
    }

    /**
     * Handle variant change event from Shopify
     */
    onVariantChange(event) {
      if (!this.productData) return;

      // Get variant from URL (Shopify updates URL on variant change)
      const urlParams = new URLSearchParams(window.location.search);
      const variantId = urlParams.get('variant');

      if (!variantId) return;

      const variant = this.productData.variants.find(v => v.id === parseInt(variantId));
      if (variant && variant.id !== this.currentVariantId) {
        this.updateComponents(variant);
        this.currentVariantId = variant.id;
      }
    }

    /**
     * Update all micro-design components with new variant data
     */
    updateComponents(variant) {
      this.updateEditionBadge(variant);
      this.updateTechSpecs(variant);
    }

    /**
     * Update Edition Badge with variant inventory and SKU
     */
    updateEditionBadge(variant) {
      const badge = document.querySelector('.tpl-micro-edition-badge');
      if (!badge) return;

      // Add updating class for animation
      badge.classList.add('variant-updating');

      // Update variant ID
      badge.dataset.variantId = variant.id;

      // Update edition current (inventory quantity)
      const editionCurrent = badge.querySelector('[data-edition="current"]');
      if (editionCurrent && variant.inventory_management === 'shopify' && variant.inventory_quantity > 0) {
        editionCurrent.textContent = variant.inventory_quantity;
      }

      // Update SKU
      const serial = badge.querySelector('.micro-badge-serial');
      if (serial && variant.sku) {
        serial.textContent = `REF ${variant.sku}`;
      } else if (serial && !variant.sku) {
        serial.style.display = 'none';
      }

      // Update scarcity warning
      const warning = badge.querySelector('[data-inventory="warning"]');
      if (variant.inventory_management === 'shopify' && variant.inventory_quantity > 0 && variant.inventory_quantity <= 5) {
        if (warning) {
          warning.textContent = `ONLY ${variant.inventory_quantity} REMAINING`;
          warning.style.display = 'inline-block';
        } else {
          // Create warning if it doesn't exist
          const newWarning = document.createElement('p');
          newWarning.className = 'micro-badge-warning';
          newWarning.dataset.inventory = 'warning';
          newWarning.textContent = `ONLY ${variant.inventory_quantity} REMAINING`;
          badge.querySelector('.micro-badge-content').appendChild(newWarning);
        }
      } else if (warning) {
        warning.style.display = 'none';
      }

      // Remove updating class after animation
      setTimeout(() => {
        badge.classList.remove('variant-updating');
      }, 300);
    }

    /**
     * Update Technical Specs with variant-specific data
     */
    updateTechSpecs(variant) {
      const techSpecs = document.querySelector('.tpl-micro-tech-specs');
      if (!techSpecs) return;

      // Add updating class for animation
      techSpecs.classList.add('variant-updating');

      // Update variant ID
      techSpecs.dataset.variantId = variant.id;

      // Check if variant has metafield data (requires Storefront API or inline JSON)
      // For now, we'll update based on variant options (like size)

      // Update dimensions based on variant title or options
      const dimensionsMap = this.getDimensionsMap();
      const variantKey = this.getVariantKey(variant);

      if (dimensionsMap && dimensionsMap[variantKey]) {
        const specs = dimensionsMap[variantKey];

        // Update dimensions
        const dimensionsEl = techSpecs.querySelector('[data-spec="dimensions"]');
        if (dimensionsEl && specs.dimensions) {
          dimensionsEl.textContent = specs.dimensions;
        }

        // Update paper type
        const paperEl = techSpecs.querySelector('[data-spec="paper_type"]');
        if (paperEl && specs.paper_type) {
          paperEl.textContent = specs.paper_type;
        }

        // Update print method
        const methodEl = techSpecs.querySelector('[data-spec="print_method"]');
        if (methodEl && specs.print_method) {
          methodEl.textContent = specs.print_method;
        }

        // Update finish
        const finishEl = techSpecs.querySelector('[data-spec="finish"]');
        if (finishEl && specs.finish) {
          finishEl.textContent = specs.finish;
        }

        // Update scale indicator
        const scaleEl = techSpecs.querySelector('[data-spec="scale"]');
        if (scaleEl && specs.dimensions) {
          const firstDim = specs.dimensions.split('×')[0].trim();
          scaleEl.textContent = firstDim;
        }

        // Show the component if it was hidden
        techSpecs.style.display = 'block';
      } else {
        // Hide if no data for this variant
        techSpecs.style.display = 'none';
      }

      // Remove updating class after animation
      setTimeout(() => {
        techSpecs.classList.remove('variant-updating');
      }, 300);
    }

    /**
     * Get variant key for mapping (e.g., "XL", "24x36", etc.)
     */
    getVariantKey(variant) {
      // Use the first option (usually size)
      if (variant.options && variant.options.length > 0) {
        return variant.options[0];
      }
      return variant.title;
    }

    /**
     * Get dimensions map from data attribute
     * Expected format: <tpl-micro-design-updater data-variant-specs='{"XL":{"dimensions":"24 × 36 inches",...}}'
     */
    getDimensionsMap() {
      const specsData = this.dataset.variantSpecs;
      if (!specsData) return null;

      try {
        return JSON.parse(specsData);
      } catch (e) {
        console.error('TPL Micro-Design: Failed to parse variant specs', e);
        return null;
      }
    }
  });
}
